import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {useDispatch, useSelector} from 'react-redux';
import Command from '../../../state/actions/command';
import {Selector} from '../../../state/selector';
import {User} from '../../../state/types/User';
import {EmployeeRate} from '../../../state/types/Dashboard';
import {DashboardProject} from '../../../state/types/Dashboard';
import {useEffect, useState} from 'react';

interface ProjectRateDialogProps {
    open: boolean;
    onClose: () => void;
    project: DashboardProject | null;
}

const ProjectRateDialog = (props: ProjectRateDialogProps) => {
    const dispatch = useDispatch();
    const users = useSelector(Selector.User.users);
    const defaultRates = useSelector(Selector.Dashboard.rates);
    const [editedRates, setEditedRates] = useState<Record<string, string>>({});

    useEffect(() => {
        if (props.open && props.project) {
            const rateMap: Record<string, string> = {};
            props.project.employees.forEach((emp) => {
                rateMap[emp.userId] = (emp.hourlyRate / 100).toFixed(2);
            });
            setEditedRates(rateMap);
        }
    }, [props.open, props.project]);

    if (!props.project) {
        return null;
    }

    const getDefaultRate = (userId: string): string => {
        const rate = defaultRates.find((r: EmployeeRate) => r.userId === userId);
        return rate ? (rate.hourlyRate / 100).toFixed(2) : '0.00';
    };

    const handleSave = (userId: string) => {
        const value = parseFloat(editedRates[userId] || '0');
        if (isNaN(value) || value < 0) {
            return;
        }
        dispatch(Command.Dashboard.setProjectEmployeeRate({
            projectId: props.project!.projectId,
            userId,
            hourlyRate: Math.round(value * 100),
        }));
    };

    const getUserName = (userId: string): string => {
        const user: User | undefined = users[userId];
        return user ? `${user.firstName} ${user.lastName}` : userId;
    };

    return (
        <Dialog open={props.open} fullWidth={true} maxWidth={'md'} onClose={props.onClose}>
            <DialogTitle>
                Projekt-Stundensätze: {props.project.customerName} / {props.project.projectName}
            </DialogTitle>
            <DialogContent>
                <Typography variant={'body2'} color={'text.secondary'} sx={{ marginBottom: 2 }}>
                    Hier können Sie projektspezifische Stundensätze festlegen.
                    Ohne Überschreibung gilt der Standard-Stundensatz.
                </Typography>
                <TableContainer>
                    <Table size={'small'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Mitarbeiter</TableCell>
                                <TableCell align={'right'}>Standard (EUR)</TableCell>
                                <TableCell align={'right'}>Projekt-Stundensatz (EUR)</TableCell>
                                <TableCell width={80} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.project.employees.map((emp) => (
                                <TableRow key={emp.userId}>
                                    <TableCell>{getUserName(emp.userId)}</TableCell>
                                    <TableCell align={'right'}>{getDefaultRate(emp.userId)}</TableCell>
                                    <TableCell align={'right'}>
                                        <TextField
                                            size={'small'}
                                            type={'number'}
                                            value={editedRates[emp.userId] || ''}
                                            onChange={(e) => setEditedRates({
                                                ...editedRates,
                                                [emp.userId]: e.target.value,
                                            })}
                                            inputProps={{ min: 0, step: 0.01 }}
                                            sx={{ width: 120 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size={'small'}
                                            variant={'contained'}
                                            startIcon={<SaveIcon />}
                                            onClick={() => handleSave(emp.userId)}
                                        >
                                            Speichern
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProjectRateDialog;
